"use client"

import { useState } from "react"
import { Heart, Trash2, Plus, Eye, EyeOff } from "@/lib/icons"
import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useTagFiltersStore } from "@/lib/tag-filters-store"
import { useTranslations } from "next-intl"

/**
 * Loved tags: favourite tags kept for later, and optionally "show only posts with a loved tag". Hiding tags moved to the Blacklist
 * (hating a tag now adds it there), so this screen no longer has a hate list.
 */
export function TagFilterManager() {
  const t = useTranslations("tagFilterManager")
  const [tagInput, setTagInput] = useState("")

  const { tagFilters, enableFilters, filterMode, addFilter, removeFilter, toggleFilters, setFilterMode } = useTagFiltersStore()

  const handleAddFilter = () => {
    if (tagInput.trim()) {
      addFilter(tagInput, "love")
      setTagInput("")
    }
  }

  const loveTags = tagFilters.filter((f) => f.type === "love")
  const mode = filterMode === "whitelist" ? "whitelist" : "blacklist"

  return (
    <Card className="p-3">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Heart className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">{t("lovedTags")}</h3>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="enable-filters" className="text-sm cursor-pointer">
            {enableFilters ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
          </Label>
          <Switch id="enable-filters" checked={enableFilters} onCheckedChange={toggleFilters} />
        </div>
      </div>

      <div className="space-y-3 mb-4">
        <div>
          <Label htmlFor="loved-tags-mode" className="text-xs text-muted-foreground mb-1 block">
            {t("filterMode")}
          </Label>
          <Select value={mode} onValueChange={(value: any) => setFilterMode(value)}>
            <SelectTrigger id="loved-tags-mode" className="h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="blacklist">{t("lovedKeepList")}</SelectItem>
              <SelectItem value="whitelist">{t("lovedTagsShowOnlyLovedTags")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex gap-2">
          <Input
            id="loved-tag-input"
            placeholder={t("addTag")}
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAddFilter()}
            className="h-8 text-sm"
          />
          <Button onClick={handleAddFilter} size="sm" className="h-8 px-2">
            <Plus className="h-3 w-3" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">{t("hiddenTagsMovedNote")}</p>
      </div>

      <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
        {loveTags.length > 0 ? (
          loveTags.map((filter) => (
            <Badge key={filter.tag} variant="default" className="gap-1 pl-2 pr-1 group">
              ♥ {filter.tag}
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 hover:bg-transparent opacity-0 group-hover:opacity-100 [@media(pointer:coarse)]:opacity-100 transition-opacity"
                onClick={() => removeFilter(filter.tag)}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </Badge>
          ))
        ) : (
          <p className="text-sm text-muted-foreground italic">{t("noLovedTags")}</p>
        )}
      </div>
    </Card>
  )
}
